import { useEffect, useRef, useState } from 'react';
import { googleSignIn } from '../api/googleSignIn';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Google Identity Services, loaded once, on the website only. */
export function loadGoogleIdentity() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window.'));
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);

  window.__ingoGsi ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error('Google did not load.')));
    script.onerror = () => {
      window.__ingoGsi = undefined;
      reject(new Error('Google could not be reached.'));
    };
    document.head.appendChild(script);
  });

  return window.__ingoGsi;
}

/** The phone app (Capacitor), where Google's web sign-in does not work inside the app's WebView. */
function onPhone() {
  return Boolean(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.());
}

/**
 * "Continue with Google" for a client or a rider (InGo ADR 0023; ingo-e0, 2
 * Oct 2026): Google's own button, shown only while Settings offers Google with
 * a web client id, and only on the website. On the phone, Google refuses its
 * web sign-in inside an app's WebView; the native half (Credential Manager,
 * ADR 0023) is not built, so the button stays hidden there.
 *
 * `onResult(result, idToken)` gets the API's answer (`signed_in` or, for a
 * rider with no account, `sign_up`); `onError(error, idToken)` a refusal.
 */
export default function GoogleSignInButton({ realm, onResult, onError, google = googleSignIn, loadIdentity = loadGoogleIdentity, phone = onPhone }) {
  const slot = useRef(null);
  const handlers = useRef({ onResult, onError });
  handlers.current = { onResult, onError };
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    if (phone()) return undefined;
    let cancelled = false;
    google.config().then(
      (config) => !cancelled && setClientId(config?.clientId ?? null),
      () => !cancelled && setClientId(null),
    );

    return () => {
      cancelled = true;
    };
  }, [google, phone]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    loadIdentity().then(
      (gsi) => {
        if (cancelled || !slot.current) return;
        gsi.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            try {
              handlers.current.onResult(await google.signIn(realm, credential), credential);
            } catch (error) {
              handlers.current.onError(error, credential);
            }
          },
        });
        gsi.renderButton(slot.current, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: 300 });
      },
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [clientId, google, loadIdentity, realm]);

  if (!clientId) return null;

  return <div ref={slot} data-testid="google-sign-in" style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }} />;
}
